import { GetStaticPaths, GetStaticProps } from "next";
import Image from "next/image";
import { NextSeo, ArticleJsonLd } from "next-seo";
import Layout from "../../components/Layout";
import Contact from "../../components/Contact";
import sdk from "../../lib/sdk";
import { Locale, PostQuery, PostSlugsQuery } from "../../interfaces";
import siteUrl from "../../lib/siteUrl";

type Props = {
  post: PostQuery["post"];
  preview?: boolean;
};

const Post = ({ post, preview }: Props) => {
  return (
    <Layout>
      <NextSeo
        title={post?.seoTitle || post?.title}
        description={post?.description || ""}
        openGraph={{ url: "/blog/" + post?.slug }}
      />
      <ArticleJsonLd
        url={siteUrl + "/blog/" + post?.slug}
        title={post?.title || ""}
        images={[
          post?.coverImage?.url || siteUrl + "/" + "logo-with-background.webp",
        ]}
        datePublished={post?.publishedAt}
        description={post?.excerpt || ""}
        publisherName="Dr. Birsel Uçkun"
        publisherLogo={siteUrl + "/" + "logo.svg"}
        authorName={["Dr. Birsel Uçkun"]}
      />
      <div className="flex flex-col items-center flex-1 px-4">
        <Image
          src={post?.coverImage?.url || "/logo-with-background.webp"}
          width={1024}
          height={576}
          alt={post?.title}
        />
        <div className="flex flex-col items-start flex-1 mt-10 space-y-5 lg:w-1/2">
          <h1 className="text-4xl font-bold font-title">{post?.title}</h1>
          <div className="text-sm text-gray-400">
            {new Date(post?.publishedAt).toLocaleDateString() ||
              post?.createdAt}
          </div>
          <div
            className="prose sm:prose lg:prose-lg xl:prose-xl"
            dangerouslySetInnerHTML={{ __html: post?.body?.html || "" }}
          />
        </div>
      </div>
      <Contact />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  let posts: PostSlugsQuery["posts"] = [];
  for (const locale of locales!) {
    const { posts: data } = await sdk.PostSlugs({
      locale: locale as Locale,
    });
    posts = [...posts, ...data];
  }

  return {
    paths: posts.map((post) => {
      return { params: { slug: post.slug }, locale: post?.locale };
    }),
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  locale,
  locales,
  preview = false,
}) => {
  if (params?.slug && typeof params.slug === "string") {
    const { posts } = await sdk.PostSlugs({ locale: locale as Locale });
    const { post } = await sdk.Post({
      id: posts.filter((post) => post.slug === params.slug)[0].id,
      locale: locale as Locale,
      fallbackLocale: locales?.filter((l) => l !== locale)[0] as Locale,
    });

    return {
      props: {
        preview,
        post: post,
        messages: (await import(`../../messages/${locale}.json`)).default,
      },
    };
  }
  return { notFound: true };
};

export default Post;
